import {
  buildPath,
  parseRouteKey,
  type ApiError,
  type CsvDownload,
  type MultipartBody,
  type PdfDownload,
  type RouteBody,
  type RouteKey,
  type RouteParams,
  type RouteQuery,
  type RouteResponse,
} from "@contractorhub/shared";
import { getAccessToken } from "./msal";

/**
 * Typed fetch over the shared API contract. The route key ("PUT /api/my/timesheet")
 * determines the body, query, path params, and response type at the call site.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fieldErrors?: Record<string, string[]>,
    readonly entryErrors?: { index: number; code: string; message: string }[]
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export class UnauthorizedError extends ApiRequestError {
  constructor(message: string) {
    super(401, "UNAUTHORIZED", message);
    this.name = "UnauthorizedError";
  }
}

/**
 * The request never reached the API, or the API never reached us: a dropped
 * connection, or a gateway that gave up on a slow function and returned its own
 * non-JSON error page. Distinct from an error the API *chose* to send, because
 * only this kind is worth retrying.
 */
export class TransientError extends ApiRequestError {
  constructor(status: number, code: string, message: string) {
    super(status, code, message);
    this.name = "TransientError";
  }
}

/** Worth retrying? True only when nothing about the request was itself wrong. */
export function isTransient(err: unknown): boolean {
  return err instanceof TransientError;
}

/** The server's message when it sent one; a neutral fallback otherwise. */
export function errorMessage(err: unknown): string {
  return err instanceof ApiRequestError ? err.message : "Something went wrong.";
}

/** Per-field messages from a 400 VALIDATION response, keyed by field name. */
export function fieldErrorsOf(err: unknown): Record<string, string[]> {
  return (err instanceof ApiRequestError ? err.fieldErrors : undefined) ?? {};
}

type RequestOptions<K extends RouteKey> = ([RouteBody<K>] extends [never]
  ? { body?: undefined }
  : { body: RouteBody<K> }) &
  ([RouteQuery<K>] extends [never] ? { query?: undefined } : { query?: RouteQuery<K> }) &
  ([RouteParams<K>] extends [never]
    ? { params?: undefined }
    : { params: RouteParams<K> }) & { signal?: AbortSignal };

/** Query values in the contract are always scalars; skip anything unset. */
type QueryValue = string | number | boolean | undefined | null;

function queryString(query: Record<string, QueryValue> | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `?${search}` : "";
}

/** The bearer header when signed in — best-effort, exactly as `apiFetch` attaches it. */
async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const token = await getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/** A `fetch` that never reached the API, translated — an abort is the caller's own doing. */
function networkFailure(err: unknown): never {
  if (err instanceof DOMException && err.name === "AbortError") throw err;
  throw new TransientError(
    0,
    "NETWORK",
    "Couldn't reach the server. Check your connection and try again."
  );
}

/**
 * Turn a non-OK response into the right error. One implementation for every
 * entry point, so a CSV download, a PDF preview and a JSON call all surface a
 * `VALIDATION` envelope the same way.
 */
async function throwEnvelope(response: Response): Promise<never> {
  let payload: ApiError | null = null;
  try {
    payload = (await response.json()) as ApiError;
  } catch {
    // Not our error envelope, so it isn't the API talking — it's the platform
    // in front of it (a 502/504 from the gateway abandoning a slow function).
  }

  if (!payload && response.status >= 500) {
    throw new TransientError(
      response.status,
      "UPSTREAM",
      "The server took too long to respond. Retrying…"
    );
  }

  const code = payload?.error.code ?? "UNKNOWN";
  const message = payload?.error.message ?? "Something went wrong.";

  if (response.status === 401) throw new UnauthorizedError(message);

  throw new ApiRequestError(
    response.status,
    code,
    message,
    payload?.error.fieldErrors,
    payload?.error.entryErrors
  );
}

/** Routes whose response is a file, not JSON. */
type FileRoute<K extends RouteKey> =
  RouteResponse<K> extends CsvDownload | PdfDownload ? K : never;

export type FetchedFile = {
  blob: Blob;
  /** What the server called it, from `Content-Disposition`. */
  filename: string | null;
  contentType: string | null;
};

/**
 * Fetch a route that streams a file rather than JSON, as a `Blob`.
 *
 * A plain `window.open(url)` cannot work here: the API authenticates with a
 * bearer token, and a top-level navigation carries cookies, not headers — it
 * would arrive unauthenticated and 401. So the file is fetched like any other
 * request. `apiDownload` hands the result to a synthetic `<a download>`; the PDF
 * preview turns it into an object URL for a frame.
 *
 * Typed so that only routes whose declared response is `CsvDownload` or
 * `PdfDownload` can be passed; `apiFetch` on such a route fails to compile,
 * because parsing a file body as JSON would throw somewhere far from the cause.
 */
export async function apiFile<K extends RouteKey>(
  route: FileRoute<K>,
  options: RequestOptions<K> = {} as RequestOptions<K>
): Promise<FetchedFile> {
  const { method, path } = parseRouteKey(route);
  const url =
    buildPath(path, options.params as Record<string, string> | undefined) +
    queryString(options.query as Record<string, QueryValue> | undefined);

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: await authHeaders(),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    networkFailure(err);
  }

  // A failed download still answers with our JSON error envelope, so it is worth
  // parsing: "narrow the date range" is actionable, "download failed" is not.
  if (!response.ok) await throwEnvelope(response);

  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("Content-Disposition")),
    contentType: response.headers.get("Content-Type"),
  };
}

/**
 * Download a file route to the user's disk.
 *
 * `fallbackFilename` is only used when the server names nothing; every file
 * route does, so it is the name of a bug rather than a feature.
 */
export async function apiDownload<K extends RouteKey>(
  route: FileRoute<K>,
  options: RequestOptions<K> = {} as RequestOptions<K>,
  fallbackFilename = "export.csv"
): Promise<void> {
  const file = await apiFile(route, options);
  const objectUrl = URL.createObjectURL(file.blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = file.filename ?? fallbackFilename;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Revoked in a `finally` so a throw between create and click cannot leak the
    // blob for the lifetime of the document.
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * The server names the file; `Content-Disposition` is the only place it says so.
 *
 * It sends both forms (RFC 6266): `filename*=UTF-8''…`, percent-encoded, which
 * carries a contractor's real file name whatever its characters, and a plain
 * `filename="…"` with an ASCII stand-in for clients that cannot read the first.
 * We can, so the encoded one wins; a malformed encoding falls back to the plain.
 */
export function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*\s*=\s*UTF-8''([^;\s]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Not valid percent-encoding — take the ASCII name instead.
    }
  }
  return header.match(/filename\s*=\s*"([^"]+)"/i)?.[1] ?? null;
}

/** Routes whose body is a multipart form. */
type UploadRoute<K extends RouteKey> =
  RouteBody<K> extends MultipartBody<Record<string, "text" | "file">> ? K : never;

/** The form fields a multipart route takes, with `"file"` tags resolved to `File`. */
export type MultipartFields<K extends RouteKey> =
  RouteBody<K> extends MultipartBody<infer F>
    ? { [P in keyof F]: F[P] extends "file" ? File : string }
    : never;

type UploadOptions<K extends RouteKey> = {
  fields: MultipartFields<K>;
  signal?: AbortSignal;
} & ([RouteParams<K>] extends [never]
  ? { params?: undefined }
  : { params: RouteParams<K> });

/**
 * Send a file (and any text fields beside it) as `multipart/form-data`.
 *
 * No `Content-Type` is set by hand: the browser writes it, boundary included,
 * and a hand-written one would omit the boundary and make the body unreadable.
 * There is no upload progress — `fetch` cannot report it — so the caller shows
 * an indeterminate busy state; the cap is small enough (see
 * `MAX_INVOICE_PDF_BYTES`) that this is a few seconds at worst.
 */
export async function apiUpload<K extends RouteKey>(
  route: UploadRoute<K>,
  options: UploadOptions<K>
): Promise<RouteResponse<K>> {
  const { method, path } = parseRouteKey(route);
  const url = buildPath(path, options.params as Record<string, string> | undefined);

  const form = new FormData();
  for (const [key, value] of Object.entries(
    options.fields as Record<string, string | File>
  )) {
    if (value instanceof File) form.append(key, value, value.name);
    else form.append(key, value);
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: await authHeaders(),
      body: form,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    networkFailure(err);
  }

  if (!response.ok) await throwEnvelope(response);
  return (await response.json()) as RouteResponse<K>;
}

export async function apiFetch<K extends RouteKey>(
  route: K,
  options: RequestOptions<K> = {} as RequestOptions<K>
): Promise<RouteResponse<K>> {
  const { method, path } = parseRouteKey(route);
  const url =
    buildPath(path, options.params as Record<string, string> | undefined) +
    queryString(options.query as Record<string, QueryValue> | undefined);

  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["Content-Type"] = "application/json";

  // Attach the Entra access token when signed in (S1.1). Best-effort: the public
  // routes (`/api/auth/config`) and the pre-sign-in state simply carry no token,
  // and a protected route with no token gets the ordinary 401 → login redirect.
  const token = await getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (err) {
    networkFailure(err);
  }

  if (response.ok) {
    // No route in the contract returns 204, so a body is always expected.
    return (await response.json()) as RouteResponse<K>;
  }

  return throwEnvelope(response);
}
